import clsx from 'clsx'

export interface LogoMarkProps {
  /** Size preset or pixel dimension */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number
  /**
   * Visual tone:
   * - 'light': Teal mark on light backgrounds
   * - 'dark': White bubble on dark teal panels
   * - 'monochrome': Single-tone (currentColor)
   */
  tone?: 'light' | 'dark' | 'monochrome'
  /**
   * Presentation style:
   * - 'tile' (default): Rounded squircle app-icon tile
   * - 'standalone': Direct vector speech-bubble mark
   */
  markStyle?: 'standalone' | 'tile'
  className?: string
  'aria-label'?: string
}

export interface LogoProps {
  /** Optional context label below the wordmark, e.g. "School Portal", "Property Portal", "Admin Console". */
  subtitle?: string
  /** Visual tone. `light` uses the teal mark on white; `dark` inverts for use on teal panels. */
  tone?: 'light' | 'dark'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Display variant: full lockup, mark only, or wordmark only */
  variant?: 'full' | 'mark' | 'wordmark'
  /** Presentation style of the mark */
  markStyle?: 'standalone' | 'tile'
  className?: string
}

/**
 * Geometric vector paths for the LipaFlow conversation bubble and heart mark.
 * Mirrored about x=32 by construction for flawless rendering from 16px to 512px.
 */
export const LIPAF_FLOW_BUBBLE_PATH =
  'M 23 10 H 41 C 48.18 10 54 15.82 54 23 V 29 C 54 36.18 48.18 42 41 42 H 28 C 24.2 42 19 46.5 15.2 51.2 C 13.8 53 11.4 52.2 11.8 49.8 C 12.5 45.8 12.2 43 11.4 41.2 C 10.5 39.2 10 35.5 10 29 V 23 C 10 15.82 15.82 10 23 10 Z'

export const LIPAF_FLOW_HEART_PATH =
  'M 32 35 C 30.5 33.2 21.8 26.4 21.8 21.2 C 21.8 17.2 24.8 14.5 28.2 14.5 C 30.1 14.5 31.3 15.4 32 16.3 C 32.7 15.4 33.9 14.5 35.8 14.5 C 39.2 14.5 42.2 17.2 42.2 21.2 C 42.2 26.4 33.5 33.2 32 35 Z'

/**
 * Standalone LipaFlow icon / mark.
 * Renders the speech bubble (conversation) + heart (care).
 */
export function LogoMark({
  size = 'md',
  tone = 'light',
  markStyle = 'tile',
  className,
  'aria-label': ariaLabel = 'LipaFlow',
}: LogoMarkProps) {
  const isNumeric = typeof size === 'number'
  const sizeMap = {
    xs: { box: 'w-6 h-6', svgStandalone: 'w-5 h-5', innerScale: '68%' },
    sm: { box: 'w-8 h-8', svgStandalone: 'w-7 h-7', innerScale: '68%' },
    md: { box: 'w-10 h-10', svgStandalone: 'w-9 h-9', innerScale: '68%' },
    lg: { box: 'w-12 h-12', svgStandalone: 'w-11 h-11', innerScale: '68%' },
    xl: { box: 'w-16 h-16', svgStandalone: 'w-14 h-14', innerScale: '68%' },
  }

  const selectedSize = !isNumeric ? sizeMap[size] : null
  const isDark = tone === 'dark'
  const isMono = tone === 'monochrome'

  // Colors
  const bubbleFill = isMono
    ? 'currentColor'
    : markStyle === 'tile'
      ? isDark
        ? '#006D77'
        : '#FFFFFF'
      : isDark
        ? '#FFFFFF'
        : '#006D77'

  const heartFill = isMono
    ? isDark
      ? '#006D77'
      : '#FFFFFF'
    : '#E29578'

  const tileBg = isDark ? 'bg-white/95' : 'bg-accent'

  const svgContent = (
    <svg
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      style={isNumeric && markStyle === 'standalone' ? { width: size, height: size } : undefined}
      className={clsx(
        'shrink-0',
        markStyle === 'standalone' && !isNumeric && selectedSize?.svgStandalone,
        markStyle === 'tile' && 'w-[68%] h-[68%]'
      )}
    >
      <path d={LIPAF_FLOW_BUBBLE_PATH} fill={bubbleFill} />
      <path d={LIPAF_FLOW_HEART_PATH} fill={heartFill} />
    </svg>
  )

  if (markStyle === 'standalone') {
    return (
      <div
        role="img"
        aria-label={ariaLabel}
        className={clsx('inline-flex items-center justify-center shrink-0', className)}
      >
        {svgContent}
      </div>
    )
  }

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={isNumeric ? { width: size, height: size } : undefined}
      className={clsx(
        'flex items-center justify-center rounded-chip shrink-0',
        !isNumeric && selectedSize?.box,
        tileBg,
        className
      )}
    >
      {svgContent}
    </div>
  )
}

/**
 * LipaFlow wordmark + conversation & care mark.
 *
 * Per Brand Identity:
 * - Mark represents respectful conversation (speech bubble) + care (heart).
 * - Bubble = Serenity Teal (#006D77), Heart = Savannah Clay (#E29578).
 * - Wordmark in Outfit font with geometric precision.
 */
export function Logo({
  subtitle,
  tone = 'light',
  size = 'md',
  variant = 'full',
  markStyle = 'tile',
  className,
}: LogoProps) {
  const dims = {
    xs: { title: 'text-body-sm', sub: 'text-[10px]' },
    sm: { title: 'text-h4', sub: 'text-caption' },
    md: { title: 'text-h3', sub: 'text-caption' },
    lg: { title: 'text-h2', sub: 'text-body-sm' },
    xl: { title: 'text-display', sub: 'text-body' },
  }[size]

  const isDark = tone === 'dark'
  const titleClass = isDark ? 'text-white' : 'text-ink'
  const subClass = isDark ? 'text-white/70' : 'text-ink-muted'

  if (variant === 'mark') {
    return (
      <LogoMark
        size={size}
        tone={tone}
        markStyle={markStyle}
        className={className}
      />
    )
  }

  if (variant === 'wordmark') {
    return (
      <div className={clsx('leading-tight', className)}>
        <span className={clsx('block font-outfit font-semibold tracking-tight', dims.title, titleClass)}>
          LipaFlow
        </span>
        {subtitle && (
          <span className={clsx('block font-inter', dims.sub, subClass)}>{subtitle}</span>
        )}
      </div>
    )
  }

  return (
    <div className={clsx('flex items-center gap-3', className)}>
      <LogoMark size={size} tone={tone} markStyle={markStyle} />
      <div className="leading-tight">
        <span className={clsx('block font-outfit font-semibold tracking-tight', dims.title, titleClass)}>
          LipaFlow
        </span>
        {subtitle && (
          <span className={clsx('block font-inter', dims.sub, subClass)}>{subtitle}</span>
        )}
      </div>
    </div>
  )
}
